import type { GetStaticProps, GetStaticPaths } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Typography } from '../../components/Typography';
import { Button } from '../../components/Button';
import BlogCard from '../../components/BlogSection/BlogCard';
import BlogImage from '../../components/BlogSection/BlogImage/BlogImage';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './news-detail.module.scss';
import PageWrapper from '../../components/PageWrapper';
import { formatBlogImageUrl } from '../../utils';
import SEO from '../../components/SEO';
import { sanitizeRichText } from '../../utils/sanitizeHtml';
import { ArticleSchema, BreadcrumbSchema } from '../../components/StructuredData';
import { getArticleKeywords, formatKeywords } from '../../data/keywords';

interface BlogPost {
  _id: string;
  title: string;
  summary?: string;
  body: string;
  coverImage?: {
    url: string;
    title?: string;
  };
  categories?: Array<{
    name: string;
    slug: string;
  }>;
  publishedAt: string;
  readingTime?: number;
  author?: {
    name: string;
    avatar?: string;
  };
}

interface BlogDetailPageProps {
  post: BlogPost;
  aboutData: any[];
  relatedPosts: any[];
}

const BlogDetailPage: React.FC<BlogDetailPageProps> = ({ post, aboutData, relatedPosts }) => {
  const router = useRouter();
  const [extractedHeadings, setExtractedHeadings] = useState<Array<{ id: string; label: string }>>(
    []
  );

  // Extract headings from blog body content
  useEffect(() => {
    if (!post?.body) return;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(post.body, 'text/html');
      const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

      const headingsArray = Array.from(headings).map((heading, index) => {
        const text = heading.textContent || '';
        const id = `heading-${index}`;
        return { id, label: text };
      });

      setExtractedHeadings(headingsArray);

      // Add IDs to headings in the rendered DOM
      const addIdsToHeadings = () => {
        setTimeout(() => {
          const blogContent = document.querySelector(`.${styles.bodyContent}`);
          if (blogContent) {
            const headingElements = blogContent.querySelectorAll('h1, h2, h3, h4, h5, h6');
            headingElements.forEach((element, index) => {
              element.setAttribute('id', headingsArray[index]?.id || `heading-${index}`);
            });
          }
        }, 300);
      };

      addIdsToHeadings();
    } catch (error) {
      console.error('Error extracting headings:', error);
    }
  }, [post?.body]);

  // Helper function to format date
  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Format related posts
  const formatBlogPosts = (apiPosts: any[]) => {
    return apiPosts.map((post) => ({
      id: post._id,
      image: formatBlogImageUrl(post.coverImage?.url),
      category: post.categories?.[0]?.name || 'General',
      readTime: post.readingTime ? `${post.readingTime} min read` : '5 min read',
      title: post.title,
      description: post.summary || 'Read more about this blog post.',
      author: {
        name: 'EPEN Team',
        avatar: '/chat/chat-admin-avatar.png',
        date: post.publishedAt
          ? formatDate(post.publishedAt)
          : formatDate(new Date().toISOString()),
      },
      slug: post.slug,
      body: post.body,
    }));
  };

  const formattedRelatedPosts = formatBlogPosts(relatedPosts);
  const showCarousel = formattedRelatedPosts.length > 2;

  // Carousel settings
  const carouselSettings = {
    dots: false,
    infinite: showCarousel,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          arrows: false,
        },
      },
    ],
  };

  // Get author info (default to EPEN Team if not available)
  const authorName = post.author?.name || 'EPEN Team';
  const authorAvatar = post.author?.avatar || '/chat/chat-admin-avatar.png';
  const publishedDate = formatDate(post.publishedAt);
  const readTime = post.readingTime || '5';

  // Share functions
  const getCurrentUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return '';
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(getCurrentUrl());
    const title = encodeURIComponent(post.title);
    const windowFeatures =
      'width=600,height=400,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`,
      'Facebook Share',
      windowFeatures
    );
  };

  const handleShareTwitter = () => {
    const url = encodeURIComponent(getCurrentUrl());
    const title = encodeURIComponent(post.title);
    const windowFeatures =
      'width=600,height=400,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';
    window.open(
      `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      'Twitter Share',
      windowFeatures
    );
  };

  const handleShareLinkedIn = () => {
    const url = encodeURIComponent(getCurrentUrl());
    const windowFeatures =
      'width=600,height=400,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      'LinkedIn Share',
      windowFeatures
    );
  };

  const handleCopyLink = () => {
    const url = getCurrentUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert('Link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy link:', err);
      });
  };

  const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - 100; // 100px offset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const siteUrl = process.env.SITE_URL || 'https://vxfx.io';
  const blogImage = post.coverImage?.url
    ? formatBlogImageUrl(post.coverImage.url)
    : `${siteUrl}/epenfav.webp`;

  // Create optimized description with call-to-action
  let blogDescription = post.summary || post.title || '';
  if (blogDescription && blogDescription.length > 120) {
    const truncated = blogDescription.substring(0, 120).trim();
    const lastSpace = truncated.lastIndexOf(' ');
    blogDescription = lastSpace > 100 
      ? `${truncated.substring(0, lastSpace)}... Read more!`
      : `${truncated}... Read the full article!`;
  } else if (blogDescription && blogDescription.length < 100) {
    blogDescription = `${blogDescription} Discover insights on video plugin development and software solutions.`;
  }

  // Get optimized keywords for the article
  const articleTags = post.categories?.map((cat) => cat.name) || [];
  const articleKeywords = getArticleKeywords(post.title, articleTags);
  const keywordsString = formatKeywords(articleKeywords);

  return (
    <>
      <SEO
        title={post.title}
        description={blogDescription}
        image={blogImage}
        type="article"
        publishedTime={post.publishedAt}
        modifiedTime={post.publishedAt}
        author={post.author?.name}
        keywords={keywordsString}
      />
      <ArticleSchema
        headline={post.title}
        description={post.summary || post.title}
        image={blogImage}
        datePublished={post.publishedAt}
        dateModified={post.publishedAt}
        author={post.author?.name ? { name: post.author.name } : undefined}
        url={`${siteUrl}/news/${router.query.slug}`}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'News', url: '/news' },
          { name: post.title, url: `/news/${router.query.slug}` },
        ]}
      />
      <PageWrapper
        aboutData={aboutData}
        title={post.title}
        titleText={''}
        headerSectionClass={styles.headerSectionClass}
        headerContainerClass={styles.headerContainerClass}
        contentContainerClass={styles.contentContainerClass}
        additionalHeaderContent={
          <>
            {/* Hero Section */}
            <div className={styles.heroSection}>
              <div className={styles.heroContainer}>
                {/* Left Content */}
                <div className={styles.heroContent}>
                  {/* Badge */}
                  <div className={styles.heroBadge}>
                    <span className={styles.heroBadgeTag}>
                      {post.categories?.[0]?.name || 'General'}
                    </span>
                    <span className={styles.heroReadTime}>{readTime} min read</span>
                  </div>

                  {/* Title */}
                  <Typography
                    variant="display-md"
                    as="h1"
                    color="primary"
                    className={styles.heroTitle}
                  >
                    {post.title}
                  </Typography>

                  {/* Description */}
                  {post.summary && (
                    <Typography
                      variant="text-xl"
                      as="p"
                      color="grayDark-400"
                      className={styles.heroDescription}
                    >
                      {post.summary}
                    </Typography>
                  )}

                  {/* Author Info */}
                  <div className={styles.heroAuthor}>
                    <div className={styles.heroAuthorAvatar}>
                      <img src={authorAvatar} alt={`${authorName} - Blog Post Author`} />
                    </div>
                    <div className={styles.heroAuthorDetails}>
                      <Typography variant="text-sm" as="span" color="primary" weight="medium">
                        {authorName}
                      </Typography>
                      <Typography variant="text-sm" as="span" color="grayDark-400">
                        Published {publishedDate}
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* Right Image */}
                {post.coverImage?.url && (
                  <div className={styles.heroImageContainer}>
                    <BlogImage
                      src={formatBlogImageUrl(post.coverImage.url)}
                      alt={post.coverImage.title || `${post.title} - Blog Post Cover Image`}
                    />
                    {/* Arrow (Desktop only) */}
                    <div className={styles.heroArrow}>
                      <svg
                        width="298"
                        height="148"
                        viewBox="0 0 298 148"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M297.557 67.7877C297.739 66.4189 296.776 65.1622 295.408 64.9807C294.039 64.7992 292.782 65.7617 292.601 67.1304L295.079 67.459L297.557 67.7877ZM159.257 144.668L159.735 142.214L159.734 142.214L159.257 144.668ZM43.1254 119.413L41.9594 121.625L41.9602 121.625L43.1254 119.413ZM9.051 3.73587C8.05889 2.77559 6.4762 2.80141 5.51593 3.7935C4.55566 4.7856 4.58147 6.3683 5.57358 7.32857L7.31229 5.53222L9.051 3.73587ZM-0.000462801 46.8841C-0.0652392 48.2633 1.0003 49.4339 2.37949 49.4987C3.7587 49.5635 4.92926 48.4979 4.99404 47.1187L2.49679 47.0014L-0.000462801 46.8841ZM38.6801 8.35C40.0366 8.60754 41.345 7.71668 41.6026 6.36018C41.8601 5.00371 40.9692 3.6953 39.6128 3.43775L39.1464 5.89386L38.6801 8.35ZM295.079 67.459C292.601 67.1304 292.601 67.1301 292.601 67.1302C292.601 67.1306 292.601 67.1309 292.6 67.1317C292.6 67.1333 292.6 67.136 292.599 67.1399C292.598 67.1476 292.597 67.1599 292.594 67.1767C292.59 67.2103 292.583 67.2618 292.573 67.3307C292.554 67.4684 292.524 67.6754 292.484 67.9469C292.404 68.49 292.281 69.2908 292.112 70.3109C291.775 72.3515 291.255 75.2667 290.525 78.7492C289.063 85.7246 286.77 94.927 283.445 103.918C280.106 112.949 275.797 121.563 270.395 127.521C265.048 133.419 258.859 136.5 251.484 135.216L251.055 137.678L250.626 140.141C260.216 141.812 268.012 137.593 274.1 130.88C280.133 124.225 284.71 114.915 288.135 105.652C291.575 96.3498 293.927 86.8892 295.418 79.7749C296.165 76.2125 296.698 73.2262 297.045 71.1266C297.219 70.0765 297.346 69.2474 297.43 68.6782C297.473 68.3936 297.504 68.1738 297.525 68.0238C297.535 67.9488 297.543 67.8912 297.549 67.8516C297.551 67.8319 297.553 67.8166 297.555 67.8059C297.556 67.8006 297.556 67.7964 297.557 67.7933C297.557 67.7918 297.557 67.7904 297.557 67.7896C297.557 67.7885 297.557 67.7877 295.079 67.459ZM251.055 137.678L251.484 135.216C244.712 134.036 240.981 127.955 238.939 117.155C236.921 106.479 236.829 92.6285 236.202 78.0324C235.583 63.6198 234.438 48.5805 230.152 36.1735C225.831 23.6651 218.197 13.5238 204.509 9.519L203.807 11.9184L203.105 14.3178C214.745 17.7235 221.44 26.2686 225.426 37.8061C229.447 49.445 230.586 63.8111 231.206 78.247C231.819 92.4995 231.92 106.941 234.026 118.083C236.109 129.101 240.435 138.366 250.626 140.141L251.055 137.678ZM203.807 11.9184L204.509 9.519C190.51 5.42323 177.917 12.1616 167.882 23.6525C157.84 35.1505 149.909 51.837 144.907 69.0762C139.903 86.3198 137.745 104.406 139.481 118.808C141.19 132.983 146.898 144.81 158.78 147.122L159.257 144.668L159.734 142.214C151.313 140.576 146.106 131.982 144.445 118.21C142.813 104.665 144.832 87.2784 149.709 70.4696C154.588 53.6562 162.243 37.7102 171.648 26.9415C181.059 16.1657 191.775 11.0031 203.105 14.3178L203.807 11.9184ZM159.257 144.668L158.779 147.122C164.468 148.229 169.557 147.188 173.941 144.46C178.265 141.769 181.769 137.524 184.564 132.411C190.141 122.209 193.209 107.977 194.142 93.4875C195.078 78.9638 193.887 63.8912 190.726 51.8839C189.147 45.8847 187.048 40.5416 184.397 36.3953C181.759 32.27 178.415 29.089 174.268 27.8959L173.577 30.2984L172.886 32.7009C175.413 33.428 177.895 35.5082 180.184 39.0889C182.46 42.6486 184.39 47.4563 185.891 53.1566C188.89 64.5484 190.06 79.0697 189.152 93.1661C188.242 107.296 185.262 120.711 180.177 130.012C177.642 134.65 174.662 138.122 171.299 140.215C167.995 142.271 164.193 143.082 159.735 142.214L159.257 144.668ZM173.577 30.2984L174.268 27.8959C166.686 25.7143 159.054 27.8381 152 31.9748C144.947 36.1108 138.184 42.4179 132.118 49.1573C126.036 55.9156 120.556 63.2228 116.092 69.4544C111.554 75.7881 108.208 80.8008 106.172 83.318L108.116 84.8899L110.06 86.4618C112.213 83.8002 115.805 78.4395 120.156 72.3661C124.58 66.1906 129.936 59.0571 135.835 52.5021C141.751 45.9282 148.115 40.0493 154.529 36.2879C160.943 32.5272 167.12 31.042 172.886 32.7009L173.577 30.2984ZM108.116 84.8899L106.172 83.318C101.677 88.8773 94.12 101.358 83.2813 110.69C77.9347 115.293 71.99 118.92 65.528 120.406C59.1318 121.878 52.0605 121.294 44.2905 117.201L43.1254 119.413L41.9602 121.625C50.7663 126.264 59.0601 127.025 66.6491 125.279C74.1724 123.548 80.8332 119.395 86.5435 114.479C97.8186 104.772 106.181 91.2599 110.06 86.4618L108.116 84.8899ZM43.1254 119.413L44.2913 117.202C36.8242 113.265 32.6945 106.553 30.3116 97.803C27.9055 88.9678 27.367 78.3785 26.8936 67.0488C26.4253 55.8398 26.0204 43.9553 23.7517 32.9993C21.4755 22.0068 17.2789 11.6997 9.051 3.73587L7.31229 5.53222L5.57358 7.32857C12.8199 14.3424 16.6941 23.575 18.8556 34.0132C21.0246 44.4878 21.4245 55.925 21.898 67.2575C22.3664 78.4693 22.9069 89.6415 25.4873 99.1168C28.0908 108.677 32.855 116.824 41.9594 121.625L43.1254 119.413ZM2.49679 47.0014C4.99404 47.1187 4.99406 47.1186 4.99406 47.1183C4.99408 47.118 4.99408 47.1176 4.99411 47.117C4.99417 47.1159 4.99425 47.1142 4.99437 47.1119C4.99457 47.1073 4.99489 47.1005 4.99533 47.0914C4.99618 47.0732 4.99746 47.0461 4.99914 47.0102C5.00253 46.9385 5.00755 46.8319 5.01414 46.6926C5.02729 46.4141 5.04665 46.0049 5.07151 45.4823C5.12121 44.4372 5.1928 42.9386 5.28041 41.1252C5.4556 37.4983 5.69477 32.6127 5.95064 27.5774C6.46573 17.4415 7.04046 6.87573 7.29768 4.56674L4.81303 4.28995L2.3284 4.01317C2.04949 6.51697 1.46612 17.3069 0.957104 27.3236C0.700925 32.3645 0.461564 37.2545 0.286226 40.8839C0.198563 42.6988 0.12689 44.1986 0.0771464 45.2448C0.0522723 45.768 0.0328931 46.1777 0.0197069 46.4567C0.0131145 46.5962 0.00806965 46.703 0.00469446 46.775C0.00299499 46.8109 0.00170791 46.8382 0.000850053 46.8565C0.000409262 46.8656 8.73441e-05 46.8725 -0.000120242 46.8772C-0.000225234 46.8795 -0.000324774 46.8812 -0.000370202 46.8824C-0.000405381 46.8829 -0.000416066 46.8834 -0.000422389 46.8837C-0.000454842 46.884 -0.000462801 46.8841 2.49679 47.0014ZM4.81303 4.28995L7.29768 4.56674C7.23984 5.08587 6.94619 5.39066 6.83041 5.48153C6.7583 5.53809 6.81249 5.47345 7.14366 5.37665C7.80149 5.18435 8.90483 5.0456 10.4618 5.0095C13.5241 4.93848 17.6571 5.27182 21.8885 5.76997C26.0937 6.26502 30.2933 6.91005 33.4486 7.43283C35.0244 7.69391 36.3355 7.92382 37.2515 8.08823C37.7094 8.17042 38.0683 8.2362 38.3119 8.28126C38.4337 8.30381 38.5266 8.32114 38.5887 8.33278C38.6197 8.33861 38.643 8.343 38.6584 8.34589C38.666 8.34733 38.6717 8.3484 38.6753 8.34909C38.6771 8.34942 38.6785 8.34968 38.6792 8.34983C38.6796 8.34991 38.6799 8.34996 38.6801 8.34998C38.6802 8.35001 38.6801 8.35 39.1464 5.89386C39.6128 3.43775 39.6124 3.43769 39.612 3.43762C39.6117 3.43754 39.6112 3.43744 39.6105 3.43734C39.6093 3.43709 39.6075 3.43676 39.6052 3.43632C39.6007 3.43546 39.5941 3.43422 39.5855 3.4326C39.5683 3.42935 39.5432 3.42461 39.5102 3.41843C39.4443 3.40608 39.3474 3.38799 39.2216 3.3647C38.9698 3.31813 38.6021 3.25074 38.1348 3.16687C37.2004 2.99917 35.867 2.76536 34.2659 2.50009C31.0674 1.97013 26.7842 1.31178 22.4731 0.80426C18.1882 0.299825 13.7713 -0.0686033 10.3459 0.0108472C8.65892 0.0499681 7.03387 0.199511 5.74078 0.577495C5.09652 0.765831 4.36959 1.05683 3.74355 1.54816C3.07378 2.07377 2.45326 2.89244 2.3284 4.01317L4.81303 4.28995Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        }
      >
        <div className={styles.blogDetailPage}>
          {/* Main Content */}
          <div className={styles.mainContentWrapper}>
            <div className={styles.contentArea}>
              {/* Table of Contents (Desktop only) */}
              <div className={styles.tableOfContents}>
                {extractedHeadings.length > 0 && (
                  <>
                    <Typography variant="text-sm" as="h3" weight="medium" color="grayDark-300">
                      Table of contents
                    </Typography>
                    <ul>
                      {extractedHeadings.map((item) => (
                        <li key={item.id}>
                          <a href={`#${item.id}`} onClick={(e) => handleTocClick(e, item.id)}>
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* Social Share */}
                <div className={styles.socialShare}>
                  <button title="Share on Facebook" onClick={handleShareFacebook}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                    </svg>
                  </button>
                  <button title="Share on Twitter" onClick={handleShareTwitter}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                    </svg>
                  </button>
                  <button title="Share on LinkedIn" onClick={handleShareLinkedIn}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                      <circle cx="4" cy="4" r="2" />
                    </svg>
                  </button>
                  <button title="Copy link" onClick={handleCopyLink}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Blog Content */}
              <div className={styles.blogContent}>
                <div
                  className={styles.bodyContent}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichText(post.body) }}
                />

                {/* Tags */}
                {post.categories && post.categories.length > 0 && (
                  <div className={styles.tags}>
                    {post.categories.map((category, index) => {
                      let tagClass = styles.tag;
                      // Apply different colors based on category name or index
                      if (category.name.toLowerCase().includes('design')) {
                        tagClass = `${styles.tag} ${styles.tagPurple}`;
                      } else if (
                        category.name.toLowerCase().includes('research') ||
                        index % 2 === 1
                      ) {
                        tagClass = `${styles.tag} ${styles.tagBlue}`;
                      } else {
                        tagClass = `${styles.tag} ${styles.tagPurple}`;
                      }

                      return (
                        <span key={category.slug} className={tagClass}>
                          {category.name}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Table of Contents & Share (Tablet/Mobile) */}
                <div className={styles.mobileTocAndShare}>
                  <div className={styles.mobileTableOfContents}>
                    {extractedHeadings.length > 0 && (
                      <>
                        <Typography variant="text-sm" as="h3" weight="medium" color="grayDark-300">
                          Table of contents
                        </Typography>
                        <ul>
                          {extractedHeadings.map((item) => (
                            <li key={item.id}>
                              <a href={`#${item.id}`} onClick={(e) => handleTocClick(e, item.id)}>
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  <div className={styles.mobileSocialShare}>
                    <button title="Share on Facebook" onClick={handleShareFacebook}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                      </svg>
                    </button>
                    <button title="Share on Twitter" onClick={handleShareTwitter}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                      </svg>
                    </button>
                    <button title="Share on LinkedIn" onClick={handleShareLinkedIn}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                        <circle cx="4" cy="4" r="2" />
                      </svg>
                    </button>
                    <button title="Copy link" onClick={handleCopyLink}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* From the Blog Section */}
          <div className={styles.fromBlogSection}>
            {formattedRelatedPosts && formattedRelatedPosts.length > 0 && (
              <>
                {/* Desktop: Title and Carousel side by side */}
                {showCarousel ? (
                  <div className={styles.fromBlogHeaderWrapper}>
                    <div className={styles.fromBlogHeaderLeft}>
                      <Typography
                        variant="text-sm"
                        as="span"
                        color="grayDark-400"
                        className={styles.latestLabel}
                      >
                        Latest
                      </Typography>
                      <Typography
                        variant="display-md"
                        as="h2"
                        color="primary"
                        className={styles.fromBlogTitle}
                      >
                        From the blog
                      </Typography>
                      <Typography
                        variant="text-md"
                        as="p"
                        color="grayDark-400"
                        className={styles.fromBlogDescription}
                      >
                        The latest industry news, interviews, technologies, and resources.
                      </Typography>
                      <Button
                        variant="primary"
                        size="md"
                        className={styles.viewAllButtonDesktop}
                        onClick={() => (window.location.href = '/news')}
                      >
                        View all posts
                      </Button>
                    </div>
                    <div className={styles.fromBlogHeaderRight}>
                      <div className={styles.fromBlogCarouselWrapper}>
                        <Slider {...carouselSettings} className={styles.fromBlogCarousel}>
                          {formattedRelatedPosts.map((relatedPost) => (
                            <BlogCard
                              key={relatedPost.id}
                              post={relatedPost}
                              noBorderRadius={true}
                            />
                          ))}
                        </Slider>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.fromBlogSimpleLayout}>
                    <div className={styles.fromBlogHeaderLeft}>
                      <Typography
                        variant="text-sm"
                        as="span"
                        color="grayDark-400"
                        className={styles.latestLabel}
                      >
                        Latest
                      </Typography>
                      <Typography
                        variant="display-md"
                        as="h2"
                        color="primary"
                        className={styles.fromBlogTitle}
                      >
                        From the blog
                      </Typography>
                      <Typography
                        variant="text-md"
                        as="p"
                        color="grayDark-400"
                        className={styles.fromBlogDescription}
                      >
                        The latest industry news, interviews, technologies, and resources.
                      </Typography>
                      <Button
                        variant="primary"
                        size="md"
                        className={styles.viewAllButtonDesktop}
                        onClick={() => (window.location.href = '/news')}
                      >
                        View all posts
                      </Button>
                    </div>
                    <div className={styles.fromBlogSimpleLayoutCards}>
                      {formattedRelatedPosts.map((relatedPost) => (
                        <BlogCard key={relatedPost.id} post={relatedPost} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile Posts */}
                <div className={styles.fromBlogMobileContainer}>
                  <Typography
                    variant="text-sm"
                    as="span"
                    color="grayDark-400"
                    className={styles.mobileLatestLabel}
                  >
                    Latest
                  </Typography>
                  <Typography
                    variant="display-md"
                    as="h2"
                    color="primary"
                    className={styles.mobileFromBlogTitle}
                  >
                    From the blog
                  </Typography>
                  <Typography
                    variant="text-md"
                    as="p"
                    color="grayDark-400"
                    className={styles.mobileFromBlogDescription}
                  >
                    The latest industry news, interviews, technologies, and resources.
                  </Typography>

                  <div className={styles.fromBlogMobilePosts}>
                    {formattedRelatedPosts
                      .slice(0, showCarousel ? 2 : formattedRelatedPosts.length)
                      .map((relatedPost) => (
                        <BlogCard key={relatedPost.id} post={relatedPost} />
                      ))}
                  </div>

                  <Button
                    variant="primary"
                    size="md"
                    className={styles.viewAllButtonMobile}
                    onClick={() => (window.location.href = '/news')}
                  >
                    View all posts
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default BlogDetailPage;

// Get all blog post slugs for static generation
export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/blog-posts?status=published`
    );
    const posts = response.data.data || [];

    const paths = posts.map((post: any) => ({
      params: { slug: post.slug },
    }));

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (error) {
    console.error('Error fetching blog posts for paths:', error);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};

// Get blog post data
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string;

  try {
    // Fetch blog post
    const postResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/blog-posts/slug/${slug}`
    );
    const post = postResponse.data.data[0];

    // Fetch about data
    const aboutResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/about-us`
    );
    const aboutData = aboutResponse.data.data || [];

    // Fetch related posts (exclude current post)
    let relatedPosts = [];
    try {
      const relatedResponse = await axios.get(
        `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/blog-posts?status=published&limit=4&sort=-publishedAt`
      );
      relatedPosts = relatedResponse.data.data?.filter((p: any) => p.slug !== slug) || [];
    } catch (error) {
      console.error('Error fetching related posts:', error);
      relatedPosts = [];
    }

    return {
      props: {
        post,
        aboutData,
        relatedPosts,
      },
      revalidate: 60, // Revalidate every 60 seconds
    };
  } catch (error) {
    console.error('Error fetching blog post:', error);

    return {
      notFound: true,
    };
  }
};
